/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { FunctionComponent, JSX, MouseEvent } from "react";

import {
  LocationUrls,
  Log,
  useUnityDispatch,
  useUnitySelector
} from "../../../viewModel";
import {
  buildProfileAvatarImageUrl,
  loadProfiles,
  PROFILE_ERROR_OPERATIONS,
  runProfileRequest,
  selectActiveProfileSelection,
  selectIsLoadingProfiles,
  selectProfilesError,
  selectProfilesImageHostBaseUrl,
  selectProfilesResponse,
  selectSelectedProfileId
} from "../../../viewModel/profiles";
import { resolveProfileName } from "../../../viewModel/profiles/profileDisplay";
import type {
  TProfileListResponse,
  TProfileResponse
} from "../../../viewModel/profiles";
import DefaultProfileImage from "../../../assets/images/defaultProfileImage.png";
import { useNavigationService } from "../../hooks/useNavigationService";
import "./ProfileMenu.scss";

const TAG: string = "ProfileMenu";

/** Tolerates a profile payload that arrives without a usable avatar. */
function resolveAvatarId(profile: TProfileResponse): string {
  const avatar = profile.attributes.avatar;
  if (avatar === undefined || avatar === null) {
    return "";
  }
  const avatarId: string | undefined = avatar.avatarId;
  return avatarId !== undefined && avatarId !== null ? avatarId : "";
}

export interface ProfileMenuProps {
  /** Whether this element has D-pad focus on SmartTV. */
  isFocused?: boolean;
  /**
   * VoiceView (opt-in, default false): the screen moves real DOM focus to the
   * next element itself, so this button must not blur on the way out — a
   * blur drops focus to <body> first and the next announcement can be lost.
   */
  syncAccessibilityFocus?: boolean;
}

/**
 * ProfileMenu — profile avatar rendered at the end of the browser MenuStrip
 * and in the top-right corner of the SmartTV HomeScreen.
 *
 * Always shows the active profile's avatar. Clicking the avatar opens the
 * profile selection screen; a caret indicator is shown only when more than
 * one profile exists on the account.
 *
 * On SmartTV, the isFocused prop enables D-pad focus handling and programmatic focus.
 */
const ProfileMenu: FunctionComponent<ProfileMenuProps> = ({
  isFocused = false,
  syncAccessibilityFocus = false
}: ProfileMenuProps): JSX.Element | null => {
  const dispatch = useUnityDispatch();
  const { navigateTo } = useNavigationService();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const profilesResponse = useUnitySelector(selectProfilesResponse);
  const imageHostBaseUrl: string = useUnitySelector(
    selectProfilesImageHostBaseUrl
  );
  const selectedProfileId: string = useUnitySelector(selectSelectedProfileId);
  const activeProfileSelection = useUnitySelector(selectActiveProfileSelection);
  const profilesError: string = useUnitySelector(selectProfilesError);
  const isLoadingProfiles: boolean = useUnitySelector(selectIsLoadingProfiles);
  // Keyed by URL, not profile id, so a refreshed image host or a changed
  // avatar is retried instead of being stuck on the default image forever.
  const [failedAvatarUrls, setFailedAvatarUrls] = useState<Set<string>>(
    new Set()
  );

  const profiles: TProfileResponse[] =
    profilesResponse !== null &&
    profilesResponse.profiles !== null &&
    profilesResponse.profiles !== undefined
      ? profilesResponse.profiles
      : [];
  const hasMultipleProfiles: boolean = profiles.length > 1;
  const hasSingleProfile: boolean = profiles.length === 1;
  const shouldShowFocus: boolean = isFocused && !hasSingleProfile;
  // selectedProfileId can still be "" right after a reload (the persisted
  // preference is loaded but not yet mirrored into profiles.selectedProfileId),
  // so fall back to the saved preference before defaulting to profiles[0].
  const persistedActiveProfileId: string =
    activeProfileSelection !== null &&
    activeProfileSelection.activeProfileId !== undefined
      ? activeProfileSelection.activeProfileId
      : "";
  const activeProfileId: string =
    selectedProfileId !== "" ? selectedProfileId : persistedActiveProfileId;
  const foundActiveProfile: TProfileResponse | undefined = profiles.find(
    (profile: TProfileResponse): boolean =>
      profile.profileId === activeProfileId
  );
  const activeProfile: TProfileResponse | undefined =
    foundActiveProfile !== undefined ? foundActiveProfile : profiles[0];

  const activeAvatarUrl: string =
    activeProfile !== undefined
      ? buildProfileAvatarImageUrl(
          imageHostBaseUrl,
          resolveAvatarId(activeProfile)
        )
      : "";
  const avatarSrc: string =
    activeAvatarUrl.length > 0 && !failedAvatarUrls.has(activeAvatarUrl)
      ? activeAvatarUrl
      : DefaultProfileImage;

  // A failed profile fetch is not fatal here — the icon keeps rendering the
  // default avatar — but it must still be traceable in the logs.
  useEffect((): void => {
    if (profilesError.length > 0) {
      Log.e(
        TAG,
        `Profiles unavailable, showing default avatar: ${profilesError}`
      );
    }
  }, [profilesError]);

  const handleAvatarError = (): void => {
    if (activeAvatarUrl.length === 0 || failedAvatarUrls.has(activeAvatarUrl)) {
      return;
    }
    Log.e(TAG, `Failed to load profile avatar image: ${activeAvatarUrl}`);
    setFailedAvatarUrls(
      (prev: Set<string>): Set<string> => new Set(prev).add(activeAvatarUrl)
    );
  };

  const goToProfileSelection = (): void => {
    try {
      navigateTo(LocationUrls.SELECT_PROFILE, undefined, {
        openedFromMenuStrip: true
      });
    } catch (error: unknown) {
      Log.e(TAG, "navigateTo(SELECT_PROFILE)", error);
    }
  };

  const handleProfileSelectionNavigation = (
    event: MouseEvent<HTMLButtonElement>
  ): void => {
    event.stopPropagation();

    if (profiles.length === 1) {
      return;
    }

    if (profiles.length > 1) {
      goToProfileSelection();
      return;
    }

    // Online but nothing to select — the earlier fetch failed or never ran, so
    // retry it here rather than pushing the user onto an empty selection screen.
    if (isLoadingProfiles) {
      Log.d(TAG, "Profiles fetch already in flight, ignoring click");
      return;
    }

    Log.i(TAG, "No profiles available, retrying profiles fetch");
    void runProfileRequest({
      dispatch: dispatch,
      methodName: "loadProfiles() retry",
      onSuccess: (response: TProfileListResponse): void => {
        const loadedProfiles: TProfileResponse[] | null | undefined =
          response.profiles;
        if (
          loadedProfiles !== null &&
          loadedProfiles !== undefined &&
          loadedProfiles.length > 1
        ) {
          goToProfileSelection();
          return;
        }
        Log.e(TAG, "loadProfiles() retry returned no profiles");
      },
      operation: PROFILE_ERROR_OPERATIONS.LOAD,
      request: (): Promise<TProfileListResponse> =>
        dispatch(loadProfiles()).unwrap(),
      tag: TAG
    });
  };

  // On SmartTV, programmatically focus this button when it becomes the active focused field.
  // preventScroll avoids the browser auto-scrolling the carousel container to
  // "reveal" the button, which sits fixed outside the scroll flow already.
  // useLayoutEffect so the native focus/blur commits before the
  // browser paints — otherwise MenuStrip's Redux-driven `--focused` class lands
  // one frame before this blur runs, showing both elements focused at once.
  useLayoutEffect((): void => {
    if (buttonRef.current === null) {
      return;
    }
    if (shouldShowFocus) {
      const scrollContainer = buttonRef.current.closest(
        ".home-screen__ui"
      ) as HTMLElement | null;
      const scrollTop: number | null =
        scrollContainer !== null ? scrollContainer.scrollTop : null;
      buttonRef.current.focus();
      if (scrollContainer !== null && scrollTop !== null) {
        scrollContainer.scrollTop = scrollTop;
      }
    } else if (!syncAccessibilityFocus) {
      // VoiceView: when the screen syncs focus, the next element takes it in
      // one step; blurring first would drop focus to <body>.
      buttonRef.current.blur();
    }
  }, [shouldShowFocus, syncAccessibilityFocus]);

  return (
    <div className="profile-menu" data-tag={TAG}>
      <button
        ref={buttonRef}
        type="button"
        className={
          "profile-menu__trigger" +
          (hasMultipleProfiles ? " profile-menu__trigger--multiple" : "") +
          (shouldShowFocus ? " profile-menu__trigger--focused" : "")
        }
        disabled={hasSingleProfile}
        aria-label={
          activeProfile !== undefined
            ? resolveProfileName(activeProfile)
            : "Profile"
        }
        onClick={handleProfileSelectionNavigation}
      >
        <img
          className="profile-menu__avatar"
          src={avatarSrc}
          alt=""
          aria-hidden="true"
          onError={handleAvatarError}
        />
        {hasMultipleProfiles ? (
          <span className="profile-menu__caret" aria-hidden="true" />
        ) : null}
      </button>
    </div>
  );
};

export default ProfileMenu;
